/**
 * Dashboard LiveMap (tipo_painel = mapalive): mapa multi-técnico + sidebar flutuante.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ticket } from 'lucide-react';
import { IconCellSignalOff } from '@tabler/icons-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import {
  fetchGoogleMapsBrowserKey,
  fetchTechnicianPanelProfilePhotoUrl,
  geocodeBatch,
} from '../../technician-panel/services/technicianPanelApi';
import { loadGoogleMaps } from '../../technician-panel/utils/technicianPanelGoogleMaps';
import './MonitoramentoMapAliveDashboard.css';

const STATUS_META = {
  em_servico: {
    id: 'em_servico',
    label: 'Em atendimento',
    short: 'Atendimento',
    color: '#34d399',
    soft: 'rgba(52, 211, 153, 0.16)',
    border: 'rgba(52, 211, 153, 0.45)',
  },
  deslocamento: {
    id: 'deslocamento',
    label: 'Em deslocamento',
    short: 'Deslocamento',
    color: '#60a5fa',
    soft: 'rgba(96, 165, 250, 0.16)',
    border: 'rgba(96, 165, 250, 0.45)',
  },
  ocioso: {
    id: 'ocioso',
    label: 'Ocioso',
    short: 'Ocioso',
    color: '#fbbf24',
    soft: 'rgba(251, 191, 36, 0.16)',
    border: 'rgba(251, 191, 36, 0.45)',
  },
  almoco: {
    id: 'almoco',
    label: 'Em almoço',
    short: 'Almoço',
    // Rosa-coral (não amarelo de ocioso #fbbf24)
    color: '#f43f5e',
    soft: 'rgba(244, 63, 94, 0.16)',
    border: 'rgba(244, 63, 94, 0.5)',
  },
  nao_alocado: {
    id: 'nao_alocado',
    label: 'Não alocado',
    short: 'Não alocado',
    color: '#a1a1aa',
    soft: 'rgba(161, 161, 170, 0.16)',
    border: 'rgba(161, 161, 170, 0.4)',
  },
};

/** Cadência GPS agressiva (poll de locations com trip). */
const POLL_LOCATIONS_FAST_MS = 5000;
/** Poll só GPS (fase 4 dual). */
const POLL_LOCATIONS_MS = 10000;
/**
 * Snapshot full (status mon + OS + GPS): mais lento — locations cobre o GPS.
 * intervalo_minutos do painel só aperta o teto (≤ 30s), não alonga.
 */
const POLL_FULL_MS = 25000;
const POLL_FULL_MAX_MS = 30000;
/** Sem sincronização / “sem sinal”: sempre após 8 min desde a última posição. */
const LIVE_STALE_MS = 8 * 60 * 1000;
const BR_CENTER = { lat: -15.78, lng: -47.93 };
/**
 * Padding em px para fitBounds / safe-area do pin.
 * top: bubble “Sem sincronização” + chip de status (~56px marker + ~50 bubble)
 * bottom: metade do pin + folga
 * right: sidebar flutuante
 */
const FIT_PAD = {
  top: 92,
  right: 340,
  bottom: 80,
  left: 36,
};
/** Raio visual do pin + folga mínima até a borda (px). */
const MARKER_SAFE_R = 36;
/** Altura máxima do chip de nome acima do pin (px). */
const MARKER_SAFE_TOP = 40;

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
}

function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.nao_alocado;
}

/**
 * Última sincronização de posição (fonte única para UI).
 * Backend: live.updated_at; em trajeto updatedAt já espelha o track.
 * Não preferir trackRecordedAt sem trip — gera “00:xx” vs “0s”.
 */
function lastSyncAt(tech) {
  if (!tech) return null;
  if (tech.updatedAt) return tech.updatedAt;
  if (tech.source === 'track' && tech.trackRecordedAt) return tech.trackRecordedAt;
  return null;
}

function activityTs(tech) {
  const raw = lastSyncAt(tech);
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatStaleSyncBubble(isoAt) {
  if (!isoAt) return null;
  const d = new Date(isoAt);
  if (Number.isNaN(d.getTime())) return null;
  const ageMs = Date.now() - d.getTime();
  if (!Number.isFinite(ageMs) || ageMs < 0) return null;
  const totalMin = Math.floor(ageMs / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours <= 0) {
    return `Sem sincronização a ${minutes} minuto${minutes === 1 ? '' : 's'}`;
  }
  return `Sem sincronização a ${hours} hora${hours === 1 ? '' : 's'} e ${minutes} minuto${minutes === 1 ? '' : 's'}`;
}

/** Limite de “sem sinal” — 8 min sobre lastSyncAt (qualquer status/trip). */
function staleThresholdMs(_tech) {
  return LIVE_STALE_MS;
}

/**
 * @returns {{ text: string, iso: string }|null}
 */
function resolveStaleInfo(tech, nowMs = Date.now()) {
  if (!tech?.hasLocation) return null;
  const at = lastSyncAt(tech);
  if (!at) return null;
  const ageMs = nowMs - new Date(at).getTime();
  if (!Number.isFinite(ageMs) || ageMs < 0) return null;
  if (ageMs <= staleThresholdMs(tech)) return null;
  const text = formatStaleSyncBubble(at);
  if (!text) return null;
  return { text, iso: at };
}

/**
 * Tooltip moderno (seta + light/dark) via portal — evita clip da sidebar (overflow / backdrop-filter).
 */
function MapAliveStaleTip({ text, children }) {
  const hostRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const measure = useCallback(() => {
    const el = hostRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({
      top: Math.round(r.top + r.height / 2),
      left: Math.round(r.left),
    });
  }, []);

  const show = useCallback(() => {
    measure();
    setOpen(true);
  }, [measure]);

  const hide = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    const onReposition = () => measure();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, measure]);

  return (
    <>
      <span
        ref={hostRef}
        className="mapalive-side-stale"
        aria-label={text}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {open && coords && typeof document !== 'undefined'
        ? createPortal(
            <span
              className="mapalive-stale-tooltip"
              role="tooltip"
              style={{ top: coords.top, left: coords.left }}
            >
              <span className="mapalive-stale-tooltip-label">{text}</span>
            </span>,
            document.body
          )
        : null}
    </>
  );
}

function techHasMapPoint(tech) {
  if (!tech?.hasLocation || tech.lat == null || tech.lng == null) return false;
  return Number.isFinite(Number(tech.lat)) && Number.isFinite(Number(tech.lng));
}

function toMapPoint(tech) {
  return { lat: Number(tech.lat), lng: Number(tech.lng), id: String(tech.id) };
}

function formatAgo(isoAt, nowMs = Date.now()) {
  if (!isoAt) return '—';
  const d = new Date(isoAt);
  if (Number.isNaN(d.getTime())) return '—';
  const sec = Math.max(0, Math.round((nowMs - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h`;
}

/** Códigos de OS do técnico (sem #). */
function techOsCodes(tech) {
  if (tech?.status === 'almoco') return [];
  const out = [];
  const push = (v) => {
    const s = typeof v === 'string' ? v.trim() : String(v?.os || '').trim();
    if (s && !out.includes(s)) out.push(s);
  };
  if (tech?.osLabel) push(tech.osLabel);
  (tech?.osEmServico || []).forEach(push);
  (tech?.osEmDeslocamento || []).forEach(push);
  if (!out.length && tech?.subtitle) {
    const m = String(tech.subtitle).match(/#(\d[\w-]*)/g);
    if (m) m.forEach((x) => push(x.replace(/^#/, '')));
  }
  return out;
}

/** Primeira OS em deslocamento (destino a enquadrar). */
function firstDeslocOs(tech) {
  if (!tech || tech.status !== 'deslocamento') return null;
  const out = [];
  const push = (v) => {
    const s = typeof v === 'string' ? v.trim() : String(v?.os || '').trim();
    if (s && !out.includes(s)) out.push(s);
  };
  (tech.osEmDeslocamento || []).forEach(push);
  if (!out.length && tech.osLabel) push(tech.osLabel);
  return out[0] || null;
}

function buildOsDestMarkerEl(dest) {
  const wrap = document.createElement('div');
  wrap.className = 'mapalive-os-pin';
  wrap.title = dest.endereco
    ? `OS ${dest.os} — ${dest.endereco}`
    : `OS ${dest.os || ''}`;
  const chip = document.createElement('span');
  chip.className = 'mapalive-os-pin-chip';
  chip.textContent = dest.os ? `OS ${dest.os}` : 'OS';
  wrap.appendChild(chip);
  const dot = document.createElement('span');
  dot.className = 'mapalive-os-pin-dot';
  wrap.appendChild(dot);
  return wrap;
}

/** Última sincronização (qualquer técnico exibido) → dd/MM/yyyy HH:mm em America/Sao_Paulo.
 * Instantes `updatedAt` vêm do BE como ISO UTC real; não usar getHours() local do browser.
 */
function formatLastSyncDdMmYyyyHhMm(technicians) {
  let latest = 0;
  for (const t of technicians || []) {
    const ts = activityTs(t);
    if (ts > latest) latest = ts;
  }
  if (!latest) return null;
  const d = new Date(latest);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    }).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    const day = get('day');
    const month = get('month');
    const year = get('year');
    let hour = get('hour');
    const minute = get('minute');
    // Alguns engines devolvem "24" à meia-noite com hour12:false.
    if (hour === '24') hour = '00';
    if (!day || !month || !year || hour === '' || minute === '') return null;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  } catch (_) {
    // Fallback: wall clock BRT (UTC−3)
    const brt = new Date(d.getTime() - 3 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(brt.getUTCDate())}/${pad(brt.getUTCMonth() + 1)}/${brt.getUTCFullYear()} ${pad(brt.getUTCHours())}:${pad(brt.getUTCMinutes())}`;
  }
}

function createPinOverlay(googleMaps, { lat, lng, el, onClick }) {
  class PinOverlay extends googleMaps.OverlayView {
    constructor() {
      super();
      this.position = new googleMaps.LatLng(lat, lng);
      this.el = el;
      this.onClick = onClick;
    }

    onAdd() {
      this.el.style.position = 'absolute';
      this.el.style.zIndex = '12';
      this.el.style.pointerEvents = 'auto';
      this.el.style.cursor = 'pointer';
      this.getPanes().overlayMouseTarget.appendChild(this.el);
      this._click = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onClick?.();
      };
      this.el.addEventListener('click', this._click);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection || !this.el) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.el.style.left = `${point.x}px`;
      this.el.style.top = `${point.y}px`;
      this.el.style.transform = 'translate(-50%, -50%)';
    }

    onRemove() {
      if (this._click) this.el?.removeEventListener('click', this._click);
      if (this.el?.parentNode) this.el.parentNode.removeChild(this.el);
      this.el = null;
    }
  }
  return new PinOverlay();
}

function shortTechName(nome) {
  const s = String(nome || '').trim();
  if (!s) return 'Técnico';
  if (s.length <= 18) return s;
  return `${s.slice(0, 16)}…`;
}

function formatSpeedKmh(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  const rounded = Math.round(n);
  return `${rounded} km/h`;
}

/**
 * OS na sidebar: 1 OS = código; N OSs em deslocamento = contagem (detalhe no foot).
 * @returns {{ kind: 'codes'|'count', codes: string[], label: string }|null}
 */
function techOsSidebarLabel(tech) {
  const codes = techOsCodes(tech);
  if (!codes.length) return null;
  if (tech.status === 'deslocamento' && codes.length > 1) {
    return {
      kind: 'count',
      codes,
      label: `${codes.length} OS`,
    };
  }
  return {
    kind: 'codes',
    codes,
    label: codes.join(', '),
  };
}

function buildMarkerEl(tech, photoUrl, { stale = false } = {}) {
  const meta = statusMeta(tech.status);
  const wrap = document.createElement('div');
  wrap.className = ['mapalive-marker', stale ? 'is-stale' : ''].filter(Boolean).join(' ');
  wrap.setAttribute('data-status', tech.status || 'nao_alocado');
  wrap.setAttribute('data-tech-id', String(tech.id));
  wrap.style.setProperty('--ma-status', meta.color);

  const label = document.createElement('span');
  label.className = 'mapalive-marker-label';

  // Em deslocamento: KM como extensão à direita do nome (único elemento).
  const speedText =
    tech.status === 'deslocamento'
      ? formatSpeedKmh(tech.speedKmh) ?? '0 km/h'
      : null;
  if (speedText) label.classList.add('mapalive-marker-label--with-speed');
  if (tech.status === 'almoco') label.classList.add('mapalive-marker-label--lunch');

  const nameChip = document.createElement('span');
  nameChip.className = 'mapalive-marker-name';
  nameChip.textContent = shortTechName(tech.nome);
  if (tech.nome) nameChip.title = String(tech.nome);
  label.appendChild(nameChip);

  if (tech.status === 'almoco') {
    const lunchChip = document.createElement('span');
    lunchChip.className = 'mapalive-marker-lunch';
    lunchChip.textContent = 'Almoço';
    lunchChip.title = 'Em almoço';
    label.appendChild(lunchChip);
  } else if (speedText) {
    const speedChip = document.createElement('span');
    speedChip.className = 'mapalive-marker-speed';
    speedChip.textContent = speedText;
    speedChip.title = speedText;
    label.appendChild(speedChip);
  }

  wrap.appendChild(label);

  const pulse = document.createElement('span');
  pulse.className = 'mapalive-marker-pulse';
  wrap.appendChild(pulse);

  const dot = document.createElement('span');
  dot.className = 'mapalive-marker-dot';
  if (photoUrl) {
    const img = document.createElement('img');
    img.className = 'mapalive-marker-img';
    img.src = photoUrl;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';
    img.addEventListener('error', () => {
      img.remove();
      dot.textContent = tech.iniciais || '•';
    });
    dot.appendChild(img);
  } else {
    dot.textContent = tech.iniciais || '•';
  }
  wrap.appendChild(dot);
  return wrap;
}

/** Intervalo do GET /mapalive (full: mon + OS). */
function desiredFullPollMs(panelIntervalMin) {
  const panelMs = Number(panelIntervalMin) > 0 ? Number(panelIntervalMin) * 60 * 1000 : null;
  // Painel em minutos antigos seria lento demais; só se for ≤ 30s.
  if (panelMs != null && panelMs >= 10000 && panelMs <= POLL_FULL_MAX_MS) {
    return Math.round(panelMs);
  }
  return POLL_FULL_MS;
}

function locLookupKeys(id) {
  const raw = String(id ?? '').trim();
  if (!raw) return [];
  const digits = raw.replace(/\D/g, '');
  const pad = digits ? digits.padStart(4, '0').slice(-4) : '';
  return [...new Set([raw, pad, digits].filter(Boolean))];
}

/** Merge GPS do poll /locations sobre snapshot full (status/OS preservados). */
function mergeLocationPatch(prevPayload, locList) {
  if (!prevPayload?.technicians?.length || !Array.isArray(locList)) return prevPayload;
  const byKey = new Map();
  for (const loc of locList) {
    if (!loc) continue;
    for (const k of locLookupKeys(loc.id)) {
      if (!byKey.has(k)) byKey.set(k, loc);
    }
  }
  let changed = false;
  const technicians = prevPayload.technicians.map((t) => {
    let loc = null;
    for (const k of locLookupKeys(t.id)) {
      loc = byKey.get(k);
      if (loc) break;
    }
    if (!loc) return t;

    // Em almoço com pin de entrada: não sobrescreve lat/lng pelo poll GPS.
    const lunchPin =
      t.status === 'almoco' &&
      (t.locationSource === 'lunch' || t.source === 'lunch') &&
      t.lat != null &&
      t.lng != null;

    if (lunchPin) {
      if (
        t.batteryPct === (loc.batteryPct ?? null) &&
        t.updatedAt === (loc.updatedAt || t.updatedAt || null)
      ) {
        return t;
      }
      changed = true;
      return {
        ...t,
        batteryPct: loc.batteryPct ?? t.batteryPct ?? null,
        updatedAt: loc.updatedAt || t.updatedAt || null,
      };
    }

    const hasLocation = !!(loc.hasLocation && loc.lat != null && loc.lng != null);
    if (
      t.hasLocation === hasLocation &&
      t.lat === (hasLocation ? loc.lat : null) &&
      t.lng === (hasLocation ? loc.lng : null) &&
      t.updatedAt === (loc.updatedAt || null) &&
      t.trackRecordedAt === (loc.trackRecordedAt || null) &&
      t.tripId === (loc.tripId || null) &&
      t.source === (loc.source || null) &&
      t.accuracyM === (loc.accuracyM ?? null) &&
      t.batteryPct === (loc.batteryPct ?? null) &&
      t.speedKmh === (loc.speedKmh ?? null)
    ) {
      return t;
    }
    changed = true;
    return {
      ...t,
      hasLocation,
      lat: hasLocation ? loc.lat : null,
      lng: hasLocation ? loc.lng : null,
      locationSource: hasLocation ? loc.source || 'live' : null,
      updatedAt: loc.updatedAt || null,
      trackRecordedAt: loc.trackRecordedAt || null,
      accuracyM: loc.accuracyM ?? null,
      batteryPct: loc.batteryPct ?? null,
      tripId: loc.tripId || null,
      speedKmh: loc.speedKmh ?? null,
      source: loc.source || null,
    };
  });
  if (!changed) return prevPayload;

  const counts = {
    em_servico: 0,
    deslocamento: 0,
    ocioso: 0,
    almoco: 0,
    nao_alocado: 0,
    com_posicao: 0,
  };
  for (const t of technicians) {
    if (counts[t.status] != null) counts[t.status] += 1;
    else counts.ocioso += 1;
    if (t.hasLocation) counts.com_posicao += 1;
  }
  const hasFastTravel = technicians.some(
    (t) => t.status === 'deslocamento' && t.tripId && t.hasLocation
  );
  return {
    ...prevPayload,
    technicians,
    counts,
    hasFastTravel,
  };
}

function latLngToContainerPixel(map, lat, lng) {
  const proj = map.getProjection?.();
  const bounds = map.getBounds?.();
  if (!proj || !bounds) return null;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const topRight = proj.fromLatLngToPoint(ne);
  const bottomLeft = proj.fromLatLngToPoint(sw);
  const world = proj.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
  if (!topRight || !bottomLeft || !world) return null;
  const scale = 2 ** map.getZoom();
  return {
    x: (world.x - bottomLeft.x) * scale,
    y: (world.y - topRight.y) * scale,
  };
}

/**
 * Mesma regra de safe-area, projetando zoom hipotético em torno do centro atual
 * (sem setZoom intermediário — evita o “vai e volta” visual).
 */
function markersFullyVisibleAtZoom(map, points, targetZoom) {
  const div = map.getDiv?.();
  if (!div) return true;
  const w = div.clientWidth;
  const h = div.clientHeight;
  if (w < 8 || h < 8) return true;

  const currentZoom = map.getZoom();
  if (typeof currentZoom !== 'number' || typeof targetZoom !== 'number') return true;

  const minX = FIT_PAD.left + MARKER_SAFE_R;
  const maxX = w - FIT_PAD.right - MARKER_SAFE_R;
  const minY = FIT_PAD.top + MARKER_SAFE_TOP;
  const maxY = h - FIT_PAD.bottom - MARKER_SAFE_R;
  const cx = w / 2;
  const cy = h / 2;
  const scale = 2 ** (targetZoom - currentZoom);

  for (const p of points) {
    const px = latLngToContainerPixel(map, p.lat, p.lng);
    if (!px) continue;
    const x = cx + (px.x - cx) * scale;
    const y = cy + (px.y - cy) * scale;
    if (x < minX || x > maxX || y < minY || y > maxY) return false;
  }
  return true;
}

function waitMapIdle(map) {
  return new Promise((resolve) => {
    window.google.maps.event.addListenerOnce(map, 'idle', () => resolve());
  });
}

async function fitMapToPoints(gmap, points, opts = {}) {
  if (!gmap || !window.google?.maps || !points?.length) return;
  const isStale = typeof opts.isStale === 'function' ? opts.isStale : () => false;

  const valid = points.filter(
    (p) =>
      Number.isFinite(Number(p.lat)) &&
      Number.isFinite(Number(p.lng)) &&
      !(Number(p.lat) === 0 && Number(p.lng) === 0)
  );
  if (!valid.length) return;

  const bounds = new window.google.maps.LatLngBounds();
  valid.forEach(({ lat, lng }) => bounds.extend({ lat: Number(lat), lng: Number(lng) }));

  if (valid.length === 1) {
    if (isStale()) return;
    gmap.setCenter({ lat: Number(valid[0].lat), lng: Number(valid[0].lng) });
    gmap.setZoom(14);
    return;
  }

  if (isStale()) return;
  gmap.fitBounds(bounds, FIT_PAD);
  await waitMapIdle(gmap);
  if (isStale()) return;

  const baseZ = gmap.getZoom();
  if (typeof baseZ !== 'number') return;

  // Escolhe o zoom mais apertado que ainda mantém os pins visíveis — só aplica 1×.
  let best = baseZ;
  for (const delta of [0.35, 0.55, 0.75]) {
    const tryZ = Math.min(baseZ + delta, 15);
    if (tryZ <= best) continue;
    if (!markersFullyVisibleAtZoom(gmap, valid, tryZ)) break;
    best = tryZ;
  }

  if (!isStale() && best !== baseZ && gmap.getZoom() !== best) {
    gmap.setZoom(best);
  }
}

export function MonitoramentoMapAliveDashboard({
  showTitle = false,
  title = 'LiveMap',
  tvCountdown = null,
  tvVisible = true,
  panelId,
  configRefreshKey = 0,
  statusFilter = null,
  onStatusOptionsChange,
  exportRef = null,
}) {
  const baseUrl = getApiBaseUrl();
  const rootRef = useRef(null);
  const setRootNode = useCallback((node) => {
    rootRef.current = node;
    if (exportRef) exportRef.current = node;
  }, [exportRef]);
  const mapHostRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const linesRef = useRef([]);
  const photosRef = useRef(new Map());
  const destCacheRef = useRef(new Map());
  const pollFullRef = useRef(null);
  const pollLocRef = useRef(null);
  const fetchFullInFlightRef = useRef(false);
  const fetchFullQueuedRef = useRef(false);
  const fetchLocInFlightRef = useRef(false);
  const fetchLocQueuedRef = useRef(false);
  const fetchGenRef = useRef(0);
  /** Último GPS de /locations para reaplicar após full (evita race full mais lento). */
  const lastLocPatchRef = useRef(null);
  const lastFitKeyRef = useRef('');
  const fitGenRef = useRef(0);
  const userPannedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  /** Destino OS por técnico: { [techId]: { os, lat, lng, endereco, loading, error } } */
  const [destByTech, setDestByTech] = useState({});
  const [mapsReady, setMapsReady] = useState(false);
  const [photosTick, setPhotosTick] = useState(0);
  /** Relógio local para hh:mm e “ago” avançarem sem esperar o poll. */
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const technicians = payload?.technicians || [];
  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);
  const selectedKey = useMemo(() => [...selectedIds].map(String).sort().join(','), [selectedIds]);

  // Exclui "Não alocado" do painel mapalive (lista, mapa e filtros).
  const panelTechnicians = useMemo(
    () => (technicians || []).filter((t) => (t.status || '') !== 'nao_alocado'),
    [technicians]
  );

  // Mais recentes primeiro; sem registro de data por último.
  const sorted = useMemo(() => {
    return [...panelTechnicians].sort((a, b) => {
      const ta = activityTs(a);
      const tb = activityTs(b);
      if (ta !== tb) return tb - ta;
      return String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');
    });
  }, [panelTechnicians]);

  const filtered = useMemo(() => {
    if (!statusFilter || !statusFilter.length) return sorted;
    const allowed = new Set(statusFilter);
    return sorted.filter((t) => allowed.has(t.status || 'ocioso'));
  }, [sorted, statusFilter]);

  // Remove da seleção quem saiu do filtro / painel.
  useEffect(() => {
    const alive = new Set(filtered.map((t) => String(t.id)));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => alive.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [filtered]);

  const selectedList = useMemo(
    () => filtered.filter((t) => selectedSet.has(String(t.id))),
    [filtered, selectedSet]
  );

  /** Técnicos selecionados em deslocamento com OS (chave estável p/ load de destino). */
  const destNeedKey = useMemo(
    () =>
      selectedList
        .filter((t) => t.status === 'deslocamento' && firstDeslocOs(t))
        .map((t) => `${t.id}:${firstDeslocOs(t)}`)
        .sort()
        .join('|'),
    [selectedList]
  );

  const toggleSelect = useCallback((id) => {
    const sid = String(id);
    setSelectedIds((prev) => (
      prev.some((x) => String(x) === sid)
        ? prev.filter((x) => String(x) !== sid)
        : [...prev, sid]
    ));
  }, []);

  // Carrega destino da OS (endereço + geocode free) ao selecionar em deslocamento.
  useEffect(() => {
    let cancelled = false;
    const needed = destNeedKey
      ? destNeedKey.split('|').map((pair) => {
        const i = pair.indexOf(':');
        return { techId: pair.slice(0, i), os: pair.slice(i + 1) };
      })
      : [];

    setDestByTech((prev) => {
      const next = {};
      needed.forEach(({ techId, os }) => {
        if (prev[techId] && String(prev[techId].os) === String(os) && !prev[techId].loading) {
          next[techId] = prev[techId];
        }
      });
      return next;
    });

    if (!needed.length) return undefined;

    (async () => {
      for (const { techId, os } of needed) {
        if (cancelled) return;
        const cacheKey = `${techId}:${os}`;
        if (destCacheRef.current.has(cacheKey)) {
          const cached = destCacheRef.current.get(cacheKey);
          setDestByTech((p) => ({ ...p, [techId]: cached }));
          continue;
        }
        setDestByTech((p) => ({
          ...p,
          [techId]: { os, loading: true, lat: null, lng: null, endereco: null, error: null },
        }));
        try {
          const res = await fetch(
            `${baseUrl}/api/monitoramento/externo/mapalive/os-destination?os=${encodeURIComponent(os)}`,
            { headers: getAuthHeaders(), cache: 'no-store' }
          );
          const json = await res.json();
          if (cancelled) return;
          if (!json.success || !json.data) {
            throw new Error(json.message || 'Destino não encontrado');
          }
          let lat = json.data.lat != null ? Number(json.data.lat) : null;
          let lng = json.data.lng != null ? Number(json.data.lng) : null;
          const endereco = json.data.endereco || json.data.enderecoLimpo || '';
          if (
            (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) &&
            (json.data.needsGeocode || endereco)
          ) {
            const address = json.data.enderecoLimpo || endereco;
            if (address) {
              const geo = await geocodeBatch([{ orderId: os, address }]);
              if (cancelled) return;
              const hit =
                (geo || []).find((g) => String(g.orderId || '') === String(os) && g.lat != null) ||
                (geo || []).find((g) => g.lat != null && g.lng != null);
              const gLat = hit?.lat != null ? Number(hit.lat) : null;
              const gLng = hit?.lng != null ? Number(hit.lng) : null;
              if (Number.isFinite(gLat) && Number.isFinite(gLng)) {
                lat = gLat;
                lng = gLng;
              }
            }
          }
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            throw new Error('Sem coordenadas do destino');
          }
          const payload = {
            os,
            lat,
            lng,
            endereco,
            loading: false,
            error: null,
            fromCache: Boolean(json.data.fromCache),
          };
          destCacheRef.current.set(cacheKey, payload);
          if (!cancelled) setDestByTech((p) => ({ ...p, [techId]: payload }));
        } catch (e) {
          if (cancelled) return;
          setDestByTech((p) => ({
            ...p,
            [techId]: {
              os,
              lat: null,
              lng: null,
              endereco: null,
              loading: false,
              error: e.message || 'Falha ao resolver destino',
            },
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [destNeedKey, baseUrl]);

  const lastSyncLabel = useMemo(
    () => formatLastSyncDdMmYyyyHhMm(filtered),
    [filtered]
  );

  useEffect(() => {
    if (typeof onStatusOptionsChange !== 'function') return;
    const ids = new Set();
    panelTechnicians.forEach((t) => ids.add(t.status || 'ocioso'));
    const order = ['em_servico', 'deslocamento', 'almoco', 'ocioso'];
    const options = order
      .filter((id) => ids.has(id))
      .map((id) => ({
        id,
        label: STATUS_META[id]?.label || id,
      }));
    [...ids].forEach((id) => {
      if (id === 'nao_alocado') return;
      if (!options.some((o) => o.id === id)) {
        options.push({ id, label: STATUS_META[id]?.label || id });
      }
    });
    onStatusOptionsChange(options);
  }, [panelTechnicians, onStatusOptionsChange]);

  // exportRef via setRootNode callback
  const clearOverlays = useCallback(() => {
    overlaysRef.current.forEach((o) => {
      try {
        o.setMap(null);
      } catch (_) {}
    });
    overlaysRef.current = [];
    linesRef.current.forEach((line) => {
      try {
        line.setMap(null);
      } catch (_) {}
    });
    linesRef.current = [];
  }, []);

  const fitPointsForSelection = useCallback((list, ids, destMap) => {
    const sel = ids?.length ? new Set(ids.map(String)) : null;
    // Com seleção: SOMENTE técnicos selecionados (+ destinos). Nunca fallback “todos”
    // (isso abria o mapa no Brasil inteiro no 1º clique).
    if (sel) {
      const points = list
        .filter((t) => sel.has(String(t.id)) && techHasMapPoint(t))
        .map(toMapPoint);
      for (const id of sel) {
        const d = destMap?.[id];
        if (
          d &&
          !d.loading &&
          Number.isFinite(Number(d.lat)) &&
          Number.isFinite(Number(d.lng)) &&
          !(Number(d.lat) === 0 && Number(d.lng) === 0)
        ) {
          points.push({
            lat: Number(d.lat),
            lng: Number(d.lng),
            id: `os:${id}:${d.os || ''}`,
          });
        }
      }
      return points;
    }
    return list.filter(techHasMapPoint).map(toMapPoint);
  }, []);

  /** Em deslocamento com OS: só enquadra depois de lat/lng do destino (ou falha). */
  const selectionAwaitingDest = useMemo(() => {
    return selectedList.some((t) => {
      if (t.status !== 'deslocamento') return false;
      const os = firstDeslocOs(t);
      if (!os) return false;
      const d = destByTech[String(t.id)];
      if (!d) return true;
      if (d.loading) return true;
      return false;
    });
  }, [selectedList, destByTech]);

  const destSig = useMemo(
    () =>
      Object.keys(destByTech)
        .sort()
        .map((id) => {
          const d = destByTech[id];
          return `${id}:${d?.os || ''}:${d?.lat ?? ''}:${d?.lng ?? ''}:${d?.loading ? 1 : 0}:${d?.error ? 1 : 0}`;
        })
        .join('|'),
    [destByTech]
  );

  const runFit = useCallback((gmap, points, key) => {
    if (!gmap || !points?.length) return;
    if (key === lastFitKeyRef.current) return;
    lastFitKeyRef.current = key;
    userPannedRef.current = false;
    const gen = ++fitGenRef.current;
    fitMapToPoints(gmap, points, {
      isStale: () => gen !== fitGenRef.current,
    });
  }, []);

  const renderMarkers = useCallback(() => {
    const gmap = mapRef.current;
    if (!gmap || !window.google?.maps) return;
    clearOverlays();

    for (const tech of filtered) {
      if (!techHasMapPoint(tech)) continue;
      const lat = Number(tech.lat);
      const lng = Number(tech.lng);
      const photo = photosRef.current.get(String(tech.id)) || null;
      const el = buildMarkerEl(tech, photo, { stale: Boolean(resolveStaleInfo(tech)) });
      if (selectedSet.has(String(tech.id))) {
        el.classList.add('is-selected');
      }
      const overlay = createPinOverlay(window.google.maps, {
        lat,
        lng,
        el,
        onClick: () => toggleSelect(tech.id),
      });
      overlay.setMap(gmap);
      overlaysRef.current.push(overlay);

      if (selectedSet.has(String(tech.id)) && tech.status === 'deslocamento') {
        const dest = destByTech[String(tech.id)];
        if (dest && Number.isFinite(Number(dest.lat)) && Number.isFinite(Number(dest.lng))) {
          const dLat = Number(dest.lat);
          const dLng = Number(dest.lng);
          const destEl = buildOsDestMarkerEl(dest);
          const destOverlay = createPinOverlay(window.google.maps, {
            lat: dLat,
            lng: dLng,
            el: destEl,
            onClick: null,
          });
          destOverlay.setMap(gmap);
          overlaysRef.current.push(destOverlay);

          const line = new window.google.maps.Polyline({
            path: [
              { lat, lng },
              { lat: dLat, lng: dLng },
            ],
            geodesic: true,
            strokeColor: '#60a5fa',
            strokeOpacity: 0,
            strokeWeight: 0,
            icons: [
              {
                icon: {
                  path: 'M 0,-1 0,1',
                  strokeOpacity: 0.85,
                  strokeColor: '#60a5fa',
                  scale: 3,
                },
                offset: '0',
                repeat: '10px',
              },
            ],
            map: gmap,
            zIndex: 8,
          });
          linesRef.current.push(line);
        }
      }
    }

    // Aguarda destino da OS em deslocamento — evita 1º fit “todos / longe” e race.
    if (selectionAwaitingDest) return;

    const fitPoints = fitPointsForSelection(filtered, selectedIds, destByTech);
    if (!fitPoints.length) return;
    const fitKey = [
      fitPoints.map((p) => `${p.id}@${Number(p.lat).toFixed(5)},${Number(p.lng).toFixed(5)}`).sort().join(';'),
      statusFilter?.join(',') || 'all',
      selectedKey || 'none',
      destSig,
    ].join('|');
    runFit(gmap, fitPoints, fitKey);
  }, [
    filtered,
    selectedIds,
    selectedKey,
    selectedSet,
    statusFilter,
    clearOverlays,
    toggleSelect,
    fitPointsForSelection,
    destByTech,
    destSig,
    selectionAwaitingDest,
    runFit,
  ]);

  const ensureMap = useCallback(async () => {
    if (mapRef.current) return mapRef.current;
    const host = mapHostRef.current;
    if (!host) return null;
    const key = await fetchGoogleMapsBrowserKey();
    if (!key) throw new Error('Google Maps key indisponível');
    await loadGoogleMaps(key);
    mapRef.current = new window.google.maps.Map(host, {
      center: BR_CENTER,
      zoom: 5,
      disableDefaultUI: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      rotateControl: false,
      scaleControl: false,
      cameraControl: false,
      keyboardShortcuts: false,
      clickableIcons: false,
      gestureHandling: 'greedy',
      isFractionalZoomEnabled: true,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });
    await waitMapIdle(mapRef.current);
    setMapsReady(true);
    return mapRef.current;
  }, []);

  const loadPhotos = useCallback(async (list) => {
    const missing = list
      .filter((t) => t?.id && !photosRef.current.has(String(t.id)))
      .slice(0, 40);
    if (!missing.length) return;
    await Promise.all(
      missing.map(async (t) => {
        try {
          const url = await fetchTechnicianPanelProfilePhotoUrl(t.id);
          photosRef.current.set(String(t.id), url || null);
        } catch (_) {
          photosRef.current.set(String(t.id), null);
        }
      })
    );
    setPhotosTick((n) => n + 1);
  }, []);

  const fetchFull = useCallback(async ({ silent = true } = {}) => {
    if (!panelId) return;
    if (fetchFullInFlightRef.current) {
      fetchFullQueuedRef.current = true;
      return;
    }
    fetchFullInFlightRef.current = true;
    const gen = fetchGenRef.current;
    if (!silent) setLoading(true);
    try {
      const qs = new URLSearchParams({
        panelId: String(panelId),
        _: String(Date.now()),
      });
      const res = await fetch(
        `${baseUrl}/api/monitoramento/externo/mapalive?${qs.toString()}`,
        {
          headers: {
            ...getAuthHeaders(),
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
          cache: 'no-store',
        }
      );
      const json = await res.json();
      if (gen !== fetchGenRef.current) return;
      if (!json.success) throw new Error(json.message || 'Erro ao carregar LiveMap');
      let next = json.data;
      // Reaplica GPS se o poll de locations for mais recente (full mais lento).
      const locPatch = lastLocPatchRef.current;
      if (locPatch?.list && next) {
        const fullAt = Date.parse(next.polledAt || next.horaAtual || '') || 0;
        if (locPatch.at >= fullAt) {
          next = mergeLocationPatch(next, locPatch.list) || next;
        }
      }
      setPayload(next);
      setError(null);
      await ensureMap();
      if (gen !== fetchGenRef.current) return;
      loadPhotos(next?.technicians || []);
    } catch (e) {
      if (gen !== fetchGenRef.current) return;
      setError(e.message || 'Erro ao carregar LiveMap');
    } finally {
      if (gen === fetchGenRef.current) {
        fetchFullInFlightRef.current = false;
        setLoading(false);
        if (fetchFullQueuedRef.current) {
          fetchFullQueuedRef.current = false;
          fetchFull({ silent: true });
        }
      }
    }
  }, [baseUrl, panelId, ensureMap, loadPhotos]);

  const fetchLocations = useCallback(async () => {
    if (!panelId) return;
    if (fetchLocInFlightRef.current) {
      fetchLocQueuedRef.current = true;
      return;
    }
    fetchLocInFlightRef.current = true;
    const gen = fetchGenRef.current;
    try {
      const qs = new URLSearchParams({
        panelId: String(panelId),
        _: String(Date.now()),
      });
      const res = await fetch(
        `${baseUrl}/api/monitoramento/externo/mapalive/locations?${qs.toString()}`,
        {
          headers: {
            ...getAuthHeaders(),
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
          cache: 'no-store',
        }
      );
      const json = await res.json();
      if (gen !== fetchGenRef.current) return;
      if (!json.success) return;
      const list = json.data?.technicians || [];
      const at = Date.parse(json.data?.polledAt || json.data?.horaAtual || '') || Date.now();
      lastLocPatchRef.current = { list, at };
      setPayload((prev) => {
        if (!prev) return prev;
        return mergeLocationPatch(prev, list) || prev;
      });
    } catch (_) {
      // GPS soft-fail: mantém último full
    } finally {
      if (gen === fetchGenRef.current) {
        fetchLocInFlightRef.current = false;
        if (fetchLocQueuedRef.current) {
          fetchLocQueuedRef.current = false;
          fetchLocations();
        }
      }
    }
  }, [baseUrl, panelId]);

  useEffect(() => {
    fetchGenRef.current += 1;
    setLoading(true);
    setPayload(null);
    setSelectedIds([]);
    setDestByTech({});
    destCacheRef.current = new Map();
    lastFitKeyRef.current = '';
    fitGenRef.current += 1;
    photosRef.current = new Map();
    lastLocPatchRef.current = null;
    fetchFullInFlightRef.current = false;
    fetchFullQueuedRef.current = false;
    fetchLocInFlightRef.current = false;
    fetchLocQueuedRef.current = false;
    fetchFull({ silent: false });
  }, [panelId, configRefreshKey, fetchFull]);

  const hasPayload = Boolean(payload);
  const fullPollMs = useMemo(
    () => (hasPayload ? desiredFullPollMs(payload?.intervalo_minutos) : POLL_FULL_MS),
    [hasPayload, payload?.intervalo_minutos]
  );
  // Cadência estável: só troca 10s ↔ 5s com hasFastTravel (não reinicia a cada GPS).
  const locPollMs = useMemo(() => {
    if (!hasPayload) return POLL_LOCATIONS_MS;
    return payload?.hasFastTravel ? POLL_LOCATIONS_FAST_MS : POLL_LOCATIONS_MS;
  }, [hasPayload, payload?.hasFastTravel]);

  // Poll full (status mon + OS) — mais lento; pausa com aba oculta.
  useEffect(() => {
    if (!hasPayload || !panelId) return undefined;
    if (pollFullRef.current) clearInterval(pollFullRef.current);
    pollFullRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchFull({ silent: true });
    }, fullPollMs);
    return () => {
      if (pollFullRef.current) clearInterval(pollFullRef.current);
      pollFullRef.current = null;
    };
  }, [fullPollMs, panelId, fetchFull, hasPayload]);

  // Poll locations (só GPS) — mais rápido; 1ª chamada imediata ao montar o timer.
  useEffect(() => {
    if (!hasPayload || !panelId) return undefined;
    if (pollLocRef.current) clearInterval(pollLocRef.current);
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
      fetchLocations();
    }
    pollLocRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchLocations();
    }, locPollMs);
    return () => {
      if (pollLocRef.current) clearInterval(pollLocRef.current);
      pollLocRef.current = null;
    };
  }, [locPollMs, panelId, fetchLocations, hasPayload]);

  // Ao voltar à aba / focar: força full + GPS.
  useEffect(() => {
    if (!panelId) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchFull({ silent: true });
        fetchLocations();
      }
    };
    const onFocus = () => {
      fetchFull({ silent: true });
      fetchLocations();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [panelId, fetchFull, fetchLocations]);

  useEffect(() => {
    if (!mapsReady) return;
    renderMarkers();
  }, [mapsReady, filtered, photosTick, selectedKey, destSig, renderMarkers]);

  useEffect(() => {
    if (!tvVisible || !mapsReady || !mapRef.current || !window.google?.maps?.event) return undefined;
    window.google.maps.event.trigger(mapRef.current, 'resize');
    return undefined;
  }, [tvVisible, mapsReady]);

  useEffect(() => {
    const host = mapHostRef.current;
    if (!host || !mapsReady) return;
    const staleIds = new Set(
      filtered.filter((t) => resolveStaleInfo(t, nowMs)).map((t) => String(t.id))
    );
    host.querySelectorAll('.mapalive-marker[data-tech-id]').forEach((el) => {
      el.classList.toggle('is-stale', staleIds.has(el.getAttribute('data-tech-id')));
    });
  }, [filtered, nowMs, mapsReady, photosTick, selectedKey, destSig]);

  useEffect(() => () => {
    clearOverlays();
    if (pollFullRef.current) clearInterval(pollFullRef.current);
    if (pollLocRef.current) clearInterval(pollLocRef.current);
  }, [clearOverlays]);

  const mapCount = filtered.filter((t) => t.hasLocation).length;

  return (
    <div
      ref={setRootNode}
      className={`mapalive-root ${showTitle ? 'mapalive-root--tv' : ''}`}
    >
      {showTitle ? (
        <div className="mapalive-tv-header">
          <div className="mapalive-tv-title">{title}</div>
          {tvCountdown ? (
            <span className="monitoramento-tv-countdown" aria-live="polite" title="Próxima troca de painel">
              {tvCountdown}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mapalive-map-shell">
        <div className="mapalive-map-host" ref={mapHostRef} data-mapalive="map" />

        {showTitle ? (
          <div
            className={`mapalive-tv-veil${mapsReady && !loading ? ' is-off' : ''}`}
            aria-hidden="true"
          />
        ) : null}

        {loading ? (
          <div className="mapalive-overlay-msg">Carregando LiveMap…</div>
        ) : null}
        {error && !loading ? (
          <div className="mapalive-overlay-msg mapalive-overlay-msg--error" role="alert">
            {error}
          </div>
        ) : null}
        {!loading && !error && !technicians.length ? (
          <div className="mapalive-overlay-msg">
            {payload?.emptyReason || 'Configure técnicos no painel para visualizar o mapa.'}
          </div>
        ) : null}
        {!loading && !error && panelTechnicians.length > 0 && !filtered.length ? (
          <div className="mapalive-overlay-msg">Nenhum técnico com o status filtrado.</div>
        ) : null}
        {!loading && !error && technicians.length > 0 && !panelTechnicians.length ? (
          <div className="mapalive-overlay-msg">Nenhum técnico alocado/ativo para exibir.</div>
        ) : null}

        <aside className="mapalive-side" aria-label="Resumo dos técnicos">
          <header className="mapalive-side-head">
            <div className="mapalive-side-meta">
              <span>
                Em mapa <strong>{mapCount}</strong> / {filtered.length}
              </span>
              <span className="mapalive-side-live">
                <span className="mapalive-side-live-dot" />
                Ao vivo
              </span>
            </div>
          </header>

          <div className="mapalive-side-list">
            {filtered.map((tech) => {
              const meta = statusMeta(tech.status);
              const isOn = selectedSet.has(String(tech.id));
              const stale = resolveStaleInfo(tech, nowMs);
              const syncAt = lastSyncAt(tech);
              return (
                <button
                  key={tech.id}
                  type="button"
                  className={['mapalive-side-row', isOn ? 'is-selected' : '', stale ? 'is-stale' : '']
                    .filter(Boolean)
                    .join(' ')}
                  style={{ '--ma-status': meta.color, '--ma-soft': meta.soft, '--ma-border': meta.border }}
                  aria-pressed={isOn}
                  onClick={() => toggleSelect(tech.id)}
                >
                  <span className="mapalive-side-avatar" aria-hidden>
                    <span className="mapalive-side-avatar-media">
                      {photosRef.current.get(String(tech.id)) ? (
                        <img src={photosRef.current.get(String(tech.id))} alt="" />
                      ) : (
                        tech.iniciais || '•'
                      )}
                    </span>
                    <span className="mapalive-side-avatar-dot" />
                  </span>
                  <span className="mapalive-side-body">
                    <span className="mapalive-side-name">{tech.nome}</span>
                    {(() => {
                      const osInfo = techOsSidebarLabel(tech);
                      if (osInfo) {
                        return (
                          <span
                            className={[
                              'mapalive-side-sub',
                              'mapalive-side-sub--os',
                              osInfo.kind === 'count' ? 'mapalive-side-sub--os-count' : '',
                            ].filter(Boolean).join(' ')}
                            title={
                              osInfo.kind === 'count'
                                ? osInfo.codes.map((c) => `#${c}`).join(' · ')
                                : undefined
                            }
                          >
                            <Ticket size={12} strokeWidth={2} aria-hidden className="mapalive-side-os-icon" />
                            <span>{osInfo.label}</span>
                          </span>
                        );
                      }
                      return (
                        <span className="mapalive-side-sub">
                          {tech.subtitle || (tech.hasLocation ? 'Com posição' : 'Sem posição')}
                        </span>
                      );
                    })()}
                  </span>
                  <span className="mapalive-side-right">
                    <span className="mapalive-side-badge-row">
                      {stale ? (
                        <MapAliveStaleTip text={stale.text}>
                          <IconCellSignalOff size={14} stroke={1.75} aria-hidden className="mapalive-side-stale-icon" />
                        </MapAliveStaleTip>
                      ) : null}
                      <span className="mapalive-side-badge">{meta.label}</span>
                    </span>
                    <span className="mapalive-side-ago">
                      {tech.hasLocation ? formatAgo(syncAt, nowMs) : '—'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {selectedList.length === 1 ? (
            <div className="mapalive-side-selection">
              <span className="mapalive-side-foot-name">{selectedList[0].nome}</span>
              <span className="mapalive-side-foot-meta">
                {statusMeta(selectedList[0].status).label}
                {(() => {
                  const osCodes = techOsCodes(selectedList[0]);
                  if (!osCodes.length) return null;
                  return (
                    <>
                      {' · '}
                      <Ticket size={11} strokeWidth={2} aria-hidden className="mapalive-side-os-icon" />
                      {' '}
                      {osCodes.map((code, i) => (
                        <span key={code}>
                          {i > 0 ? ' · ' : ''}
                          {code}
                        </span>
                      ))}
                    </>
                  );
                })()}
                {selectedList[0].hasLocation
                  ? ` · ${formatAgo(lastSyncAt(selectedList[0]), nowMs)}`
                  : ' · sem GPS'}
              </span>
            </div>
          ) : selectedList.length > 1 ? (
            <div className="mapalive-side-selection">
              <span className="mapalive-side-foot-name">
                {selectedList.length} técnicos selecionados
              </span>
              <span className="mapalive-side-foot-meta">
                {selectedList
                  .slice(0, 3)
                  .map((t) => t.nome)
                  .join(', ')}
                {selectedList.length > 3 ? ` +${selectedList.length - 3}` : ''}
              </span>
            </div>
          ) : null}

          <footer className="mapalive-side-sync" aria-label="Última sincronização">
            {lastSyncLabel
              ? `Última sincronização ${lastSyncLabel}`
              : 'Sem sincronização registrada'}
          </footer>
        </aside>
      </div>
    </div>
  );
}
